import { inject, DestroyRef, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import { Api } from '@api/api';
import {
  checkOtp,
  login as loginFn,
  loginConfirm as loginConfirmFn,
  logout as logoutFn,
  init as resetPasswordInitFn,
  resetPasswordConfirm as resetPasswordConfirmFn,
} from '@api/functions';
import type {
  CustomerDto,
  LoginConfirmDto,
  LoginRequestDto,
  OtpConfirmDto,
  ResetPasswordConfirmDto,
  ResetPasswordInitDto,
} from '@api/models';
import { environment } from '../../environments/environment';
import { CustomerService } from './customer.service';
import { AnalyticsService } from './analytics.service';
import { AuthSyncService } from './auth-sync.service';

@Service()
export class AuthService {
  private api = inject(Api);
  private destroyRef = inject(DestroyRef);
  private customerService = inject(CustomerService);
  private analyticsService = inject(AnalyticsService);
  private authSyncService = inject(AuthSyncService);

  login(loginDto: LoginRequestDto) {
    loginDto.ci = this.analyticsService.getCi();
    loginDto.si = this.analyticsService.getSi();
    return this.api.invoke(loginFn, { body: loginDto });
  }

  loginConfirm(loginConfirmDto: LoginConfirmDto) {
    return this.api.invoke(loginConfirmFn, { body: loginConfirmDto })
      .pipe(tap(response => this.signIn(response)));
  }

  logout(): void {
    this.api.invoke(logoutFn)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.customerService.setCustomer(null);
        this.authSyncService.notifySignedOut();
      });
  }

  resetPasswordInit(loginValue: string) {
    const dto: ResetPasswordInitDto = {
      login: loginValue,
    };
    return this.api.invoke(resetPasswordInitFn, { body: dto });
  }

  resetPasswordOtp(dto: OtpConfirmDto) {
    return this.api.invoke(checkOtp, { body: dto });
  }

  resetPasswordConfirm(dto: ResetPasswordConfirmDto) {
    return this.api.invoke(resetPasswordConfirmFn, { body: dto })
      .pipe(tap(response => this.signIn(response)));
  }

  getSocialLoginUrl(provider: 'google' | 'facebook'): string {
    return `${environment.apiHost}/api/v1/auth/login/${provider}`;
  }

  private signIn(customer: CustomerDto): void {
    this.customerService.setCustomer(customer);
    this.authSyncService.notifySignedIn();
  }
}
